import type { ComponentPropsWithRef } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { AlertInline, Box, Spinner, theme, usePrevious } from '@frameio/vapor';

const FADE_DURATION_MS = 300;

export type LoadingContainerProps = {
  /** Boolean indicating the current loading state, rendering the spinner */
  isLoading: boolean;
  /** Optionally disable the spinner from appearing at all */
  disableSpinner?: boolean;
  /** Animation duration (in ms) for fading in content after loading. */
  fadeAnimationDuration?: number;
  /** The delay in ms before the spinner appears visually while isLoading is true. */
  spinnerDelay?: number;
  /** Optionally offset the spinner vertically (pixels) */
  spinnerOffset?: number;
  /**
   * An error message to render within an `AlertInline` component.
   * This will only be visible if loading is also false, and it prevents the rendering
   * of all children.
   **/
  errorMessage?: string;
  /**
   * The height defaults to 100% and that style persist after loading. Use this prop to
   * switch to another height once loading is complete.
   */
  heightAfterLoad?: ComponentPropsWithRef<typeof Box>['height'];
  /** Attach a test-id to the inline alert component. Used for testing.  */
  'data-testid-error'?: string;
  /** Attach a testid to the element container. */
  'data-testid'?: string;
} & ComponentPropsWithRef<typeof Box>;

/**
 * Render a loading indicator while the wrapped component is loading. Once
 * the `isLoading` is false, the wrapped component is faded in.
 *
 * @example
 * <Loading isLoading={loading}>
 *   <Content />
 * </Loading>
 *
 * @example
 * <Loading isLoading={false} errorMessage='Something went wrong'>
 *   <Content />
 * </Loading>
 */

export const LoadingContainer = forwardRef(
  (
    {
      height = '100%',
      children,
      heightAfterLoad,
      isLoading = false,
      errorMessage,
      disableSpinner = false,
      fadeAnimationDuration = FADE_DURATION_MS,
      spinnerDelay = 300,
      spinnerOffset,
      'data-testid': dataTestId = 'loading-container-2',
      'data-testid-error': dataTestIdError,
      style,
      ...other
    }: LoadingContainerProps,
    forwardedRef: any
  ) => {
    const showContent = !isLoading;

    const prevShowContent = usePrevious(showContent);

    let heightToUse = height;
    if (heightAfterLoad && !isLoading) {
      heightToUse = heightAfterLoad;
    }

    return (
      <>
        <LoadingOpacity
          ref={forwardedRef}
          $hasError={Boolean(errorMessage)}
          data-testid={dataTestId}
          height={heightToUse}
          {...other}
          aria-hidden={isLoading}
          style={{
            ...style,
            pointerEvents: isLoading ? 'none' : undefined,
            opacity: isLoading ? 0 : 1,
            transition:
              prevShowContent !== showContent && showContent
                ? `${fadeAnimationDuration}ms ${theme.transitions.eases.easeOut} 0.1s`
                : 'none',
          }}
        >
          {errorMessage ? (
            <AlertInline data-testid={dataTestIdError} variant="error">
              {errorMessage}
            </AlertInline>
          ) : (
            children
          )}
        </LoadingOpacity>
        {!disableSpinner && (
          <Spinner
            aria-label="Content loading"
            centered={spinnerOffset ?? true}
            delay={spinnerDelay}
            isLoading={isLoading}
          />
        )}
      </>
    );
  }
);

const LoadingOpacity = styled(Box)<{ $hasError: boolean }>`
  ${(p) =>
    p.$hasError &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 ${theme.spaces[16]};
  `}
`;
